import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import goalService from '../../services/goalService';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import ProgressChart from './components/ProgressChart';
import ContributionHistoryTable from './components/ContributionHistoryTable';
import MilestoneCard from './components/MilestoneCard';
import AddContributionModal from './components/AddContributionModal';

const GoalDetailsProgress = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const goalId = searchParams?.get('goalId');

  const [goal, setGoal] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [projectedCompletion, setProjectedCompletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddContribution, setShowAddContribution] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (user?.id && goalId) {
      loadData();
    } else if (!goalId) {
      navigate('/financial-goals');
    }
  }, [user?.id, goalId, refreshKey]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [goalData, milestonesData, contributionsData, projectedDate] = await Promise.all([
        goalService?.getGoalById(goalId),
        goalService?.getMilestones(goalId),
        goalService?.getContributions(goalId),
        goalService?.getProjectedCompletion(goalId)
      ]);
      setGoal(goalData);
      setMilestones(milestonesData);
      setContributions(contributionsData);
      setProjectedCompletion(projectedDate);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContribution = async (contributionData) => {
    try {
      await goalService?.addContribution({
        ...contributionData,
        goalId
      });
      setRefreshKey(prev => prev + 1);
      setShowAddContribution(false);
    } catch (err) {
      throw err;
    }
  };

  const progress = goal?.targetAmount > 0 
    ? (goal?.currentAmount / goal?.targetAmount * 100) 
    : 0;
  const remaining = goal?.targetAmount > 0 ? goal?.targetAmount - goal?.currentAmount : 0;

  const getDaysUntilDeadline = () => {
    if (!goal?.deadline) return null;
    const today = new Date();
    const deadline = new Date(goal?.deadline);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysUntilDeadline();

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Financial Goals', path: '/financial-goals' },
    { label: 'Goal Details', path: '/goal-details-progress' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-16" />
          <p className="text-lg text-muted-foreground">Loading goal details...</p>
        </div>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-16" />
          <p className="text-lg text-foreground mb-16">Goal not found</p>
          <button
            onClick={() => navigate('/financial-goals')}
            className="px-20 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px]"
          >
            Back to Goals
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{goal?.goalName} - Goal Details - Rovyn Budget</title>
        <meta name="description" content="Track progress, contributions, and milestones for your financial goal" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="mx-auto px-16 md:px-24 lg:px-32 py-24 md:py-32 lg:py-40">
            <Breadcrumbs items={breadcrumbItems} />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-32 gap-16">
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-8">
                  {goal?.goalName}
                </h1>
                <p className="text-base md:text-lg text-muted-foreground font-caption">
                  {goal?.goalType === 'savings_target' ? 'Savings Target' : 'Debt Payoff'}
                </p>
              </div>

              <div className="flex gap-12">
                <button
                  onClick={() => navigate('/financial-goals')}
                  className="px-20 py-12 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth font-medium min-h-[44px]"
                >
                  Back to Goals
                </button>
                <button
                  onClick={() => setShowAddContribution(true)}
                  className="flex items-center gap-8 px-20 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px]"
                >
                  <Icon name="Plus" size={20} color="currentColor" />
                  Add Contribution
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-24 p-16 bg-destructive/10 border border-destructive rounded-lg">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Progress Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 md:gap-20 lg:gap-24 mb-32">
              <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2">
                <div className="flex items-center mb-12">
                  <Icon name="Target" size={24} color="var(--color-primary)" />
                  <span className="ml-8 text-sm text-muted-foreground">Target Amount</span>
                </div>
                <p className="text-2xl font-heading font-semibold text-card-foreground">
                  ${goal?.targetAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>

              <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2">
                <div className="flex items-center mb-12">
                  <Icon name="TrendingUp" size={24} color="var(--color-success)" />
                  <span className="ml-8 text-sm text-muted-foreground">Current Progress</span>
                </div>
                <p className="text-2xl font-heading font-semibold text-card-foreground">
                  ${goal?.currentAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-success mt-4">{progress?.toFixed(1)}% complete</p>
              </div>

              <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2">
                <div className="flex items-center mb-12">
                  <Icon name="DollarSign" size={24} color="var(--color-accent)" />
                  <span className="ml-8 text-sm text-muted-foreground">Remaining</span>
                </div>
                <p className="text-2xl font-heading font-semibold text-card-foreground">
                  ${remaining?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>

              <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2">
                <div className="flex items-center mb-12">
                  <Icon name="Calendar" size={24} color="var(--color-warning)" />
                  <span className="ml-8 text-sm text-muted-foreground">Deadline</span>
                </div>
                {goal?.deadline ? (
                  <>
                    <p className="text-2xl font-heading font-semibold text-card-foreground">
                      {daysRemaining !== null && daysRemaining >= 0 ? `${daysRemaining} days` : 'Overdue'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-4">
                      {new Date(goal?.deadline)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </>
                ) : (
                  <p className="text-lg text-muted-foreground">No deadline set</p>
                )}
              </div>
            </div>

            {/* Projected Completion */}
            {projectedCompletion && (
              <div className="bg-primary/10 border border-primary rounded-lg p-20 mb-32">
                <div className="flex items-center gap-12">
                  <Icon name="Calendar" size={24} color="var(--color-primary)" />
                  <div>
                    <p className="text-sm text-muted-foreground">Projected Completion Date</p>
                    <p className="text-lg font-heading font-semibold text-foreground">
                      {new Date(projectedCompletion)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Progress Chart */}
            <div className="mb-32">
              <ProgressChart contributions={contributions} goal={goal} key={refreshKey} />
            </div>

            {/* Milestones */}
            <div className="mb-32">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-20">
                Milestones
              </h2>
              {milestones?.length === 0 ? (
                <div className="bg-card rounded-lg p-24 text-center">
                  <Icon name="Award" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-12" />
                  <p className="text-muted-foreground">No milestones set for this goal</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 md:gap-20">
                  {milestones?.map((milestone) => (
                    <MilestoneCard key={milestone?.id} milestone={milestone} />
                  ))}
                </div>
              )}
            </div>

            {/* Contribution History */}
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-20">
                Contribution History
              </h2>
              <ContributionHistoryTable contributions={contributions} key={refreshKey} />
            </div>
          </div>
        </main>
      </div>

      {showAddContribution && (
        <AddContributionModal
          goal={goal}
          onClose={() => setShowAddContribution(false)}
          onAdd={handleAddContribution}
        />
      )}
    </>
  );
};

export default GoalDetailsProgress;